/**
 * useTaskGroupHero Hook
 *
 * Feature: 650-task-grouping
 *
 * Fetches the hero image for a task group's critique session.
 * Extracted from TaskGroupHeader for use in TaskGroup layout.
 */

import { gql, useQuery } from '@apollo/client';
import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { decodeGlobalId, encodeGlobalId } from '@206mp/shared/utils/globalObjectId';
import type { ImageVariant } from '@/components/photos/AdaptivePhoto';
import type { TaskGroup } from '@/types/task-groups';
import logger from '@/utils/logger';

/**
 * Convert sourceId to critique_sessions format.
 */
function getCritiqueSessionId(sourceId: string | null): string | null {
  if (!sourceId) return null;

  const decoded = decodeGlobalId(sourceId);
  if ('error' in decoded) {
    logger.warn('useTaskGroupHero', 'Failed to decode sourceId', { sourceId, error: decoded.error });
    return sourceId;
  }

  if (decoded.type === 'critique_sessions') {
    return sourceId;
  }

  return encodeGlobalId('critique_sessions', decoded.id);
}

const GET_CRITIQUE_SESSION_HERO = gql`
  query GetCritiqueSessionHero($id: ID!) {
    critiqueSession(id: $id) {
      id
      globalId
      title
      collection {
        id
        title
        heroPhotoId
        heroPhoto {
          id
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
          }
        }
        systemHeroPhoto {
          id
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
          }
        }
        items(limit: 1) {
          items {
            id
            photo {
              id
              variants {
                id
                variantType
                storageUrl
                publicUrl
                width
                height
              }
            }
          }
        }
      }
    }
  }
`;

interface CritiqueSessionHeroData {
  critiqueSession: {
    id: string;
    globalId: string;
    title: string;
    collection: {
      id: string;
      title: string;
      heroPhotoId: string | null;
      heroPhoto: {
        id: string;
        variants: ImageVariant[];
      } | null;
      systemHeroPhoto: {
        id: string;
        variants: ImageVariant[];
      } | null;
      items: {
        items: Array<{
          id: string;
          photo: {
            id: string;
            variants: ImageVariant[];
          } | null;
        }>;
      };
    };
  };
}

export interface TaskGroupHeroResult {
  heroPhoto: { id: string; variants: ImageVariant[] } | null;
  sessionTitle: string | null;
  isLoading: boolean;
  error: Error | null;
}

export function useTaskGroupHero(group: TaskGroup): TaskGroupHeroResult {
  const sessionId = getCritiqueSessionId(group.sourceId);

  const { data: sessionData, loading: sessionLoading, error: sessionError } = useQuery<CritiqueSessionHeroData>(
    GET_CRITIQUE_SESSION_HERO,
    {
      variables: { id: sessionId || 'skip-placeholder' },
      skip: !sessionId,
      fetchPolicy: 'cache-first',
    }
  );

  if (sessionError) {
    logger.error('useTaskGroupHero', 'Failed to fetch critique session', {
      sessionId,
      error: sessionError.message,
    });
  }

  const collection = sessionData?.critiqueSession?.collection;
  const systemHeroPhoto = collection?.systemHeroPhoto;
  const heroPhotoFromQuery = collection?.heroPhoto;
  const heroPhotoId = collection?.heroPhotoId;
  const firstCollectionPhoto = collection?.items?.items?.[0]?.photo;

  const { data: heroPhotoFallback, isLoading: heroPhotoFallbackLoading } = usePhotoByIdGraphQL(
    !systemHeroPhoto && !heroPhotoFromQuery && heroPhotoId ? heroPhotoId : undefined
  );

  const collectionHeroPhoto = systemHeroPhoto || heroPhotoFromQuery || heroPhotoFallback || firstCollectionPhoto;

  const firstTaskPhotoId = !collectionHeroPhoto && group.tasks.length > 0
    ? (group.tasks[0].targetContext && 'photoId' in group.tasks[0].targetContext
        ? String(group.tasks[0].targetContext.photoId)
        : null)
    : null;

  const { data: taskPhotoData, isLoading: taskPhotoLoading } = usePhotoByIdGraphQL(
    firstTaskPhotoId ?? undefined
  );

  const heroPhoto = collectionHeroPhoto || taskPhotoData || null;
  const isLoading = sessionLoading || heroPhotoFallbackLoading || (!!firstTaskPhotoId && taskPhotoLoading);
  const sessionTitle = sessionData?.critiqueSession?.title ?? null;

  return {
    heroPhoto,
    sessionTitle,
    isLoading,
    error: sessionError ?? null,
  };
}
